import React, { type ComponentProps, useCallback } from 'react';
import { Link, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, Text } from 'react-native';
import type { SharedValue } from 'react-native-reanimated';

import { CustomNavigationHeader, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentNavigationHeader = (props: MenuContentNavigationHeaderProps) => {
  const {
    orderChannel,
    locationName,
    scrollOffsetSV,
    safeAreaEdges,
    testID,
    onBackBtnPressOverride,
    onCloseBtnPressOverride,
  } = props;

  const { minWidth } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Navigation ──────────────────────────────────────────────────────

  const router = useRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.navigate('/locations');
  }, [router]);

  const onBackBtnPress = useCallback(() => {
    if (onBackBtnPressOverride) {
      onBackBtnPressOverride();

      return;
    }

    goBack();
  }, [goBack, onBackBtnPressOverride]);

  const onCloseBtnPress = useCallback(() => {
    if (onCloseBtnPressOverride) {
      onCloseBtnPressOverride();

      return;
    }

    goBack();
  }, [goBack, onCloseBtnPressOverride]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldRenderBackButton =
    onBackBtnPressOverride !== undefined || (router.canGoBack() ?? false);

  // ─────────────────────────────────────────────────────────────────────

  // NOTE: Custom header is only used on the smallest breakpoint.
  if (minWidth.isSM) {
    return null;
  }

  return (
    <CustomNavigationHeader.Container
      transition="gradient"
      testID={testID}
      safeAreaEdges={safeAreaEdges}
      scrollOffsetSV={scrollOffsetSV}
    >
      {shouldRenderBackButton ? (
        <CustomNavigationHeader.Button.Left
          name="IconArrowLeft"
          accessibilityLabel={formatMessage(messages.goBackButtonA11yLabel)}
          onPress={onBackBtnPress}
        />
      ) : null}

      <CustomNavigationHeader.Text textAlignment="center" numberOfLines={1}>
        <Link href="/locations">
          <Text style={styles.link}>
            <FormattedMessage
              {...messages.orderChannelText}
              values={{ order_channel: orderChannel }}
            />
          </Text>
        </Link>

        <Text>
          <FormattedMessage
            {...messages.orderChannelSucceedingText}
            values={{ order_channel: orderChannel }}
          />
        </Text>

        <Link href="/locations">
          <Text style={styles.link}>{locationName}</Text>
        </Link>
      </CustomNavigationHeader.Text>

      <CustomNavigationHeader.Button.Right
        testID="menu-content.navigation-header.close-button"
        name="IconClose"
        accessibilityLabel={formatMessage(messages.closeButtonA11yLabel)}
        onPress={onCloseBtnPress}
      />
    </CustomNavigationHeader.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderChannelText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup}
      delivery {Delivery}
      outpost {Outpost}
      other {}
    }`,
    description: 'Menu | Navigation Header | Text | Order channel after text',
  },
  orderChannelSucceedingText: {
    defaultMessage: `{order_channel, select,
      pickup { from }
      delivery { to }
      outpost { at }
      other { }
    }`,
    description: 'Menu | Navigation Header | Text | Order channel succeeding text',
  },
  goBackButtonA11yLabel: {
    defaultMessage: 'Go back',
    description: 'Menu | Navigation Header | Back button | a11y label',
  },
  closeButtonA11yLabel: {
    defaultMessage: 'Close',
    description: 'Menu | Navigation Header | Close button | a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    color: theme.colors.SPINACH,
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentNavigationHeaderProps = {
  testID?: string;
  orderChannel: 'pickup' | 'outpost' | 'delivery';
  locationName: string;
  onBackBtnPressOverride?: () => void;
  onCloseBtnPressOverride?: () => void;

  /**
   * An optional shared value of the associated scroll view offset, which can be
   * used for animation.
   */
  scrollOffsetSV?: SharedValue<number>;
  safeAreaEdges?: ComponentProps<typeof CustomNavigationHeader.Container>['safeAreaEdges'];
};
